/**
 * Curve taper evaluation — sampling a "taper object" curve to produce a
 * radius-multiplier profile along a target curve.
 *
 * Follows Blender's convention: the taper curve's local-space X extent maps
 * to [0, 1] along the target's arc length. The taper curve's Y value at
 * that X position is the radius multiplier, clamped to ≥ 0.
 *
 * Pure module — no scene-graph access. Callers resolve the taper curve by
 * name and pass its `CurveData` in.
 */

import { evaluateCurve } from "./curvemath.ts";
import type { CurveData } from "./types.ts";

/**
 * Compute a per-sample radius multiplier array for a target curve given a
 * taper-source curve.
 *
 * Returns `null` when the taper curve cannot produce a profile — fewer than
 * two samples or a zero X extent — which signals callers to apply the
 * uniform (no-taper) radius.
 */
export function computeTaperScale(
  targetSampleCount: number,
  taperCurve: CurveData,
): Float32Array | null {
  if (targetSampleCount < 1) return null;

  const { positions } = evaluateCurve(taperCurve);
  const n = positions.length;
  if (n < 2) return null;

  let minX = positions[0][0];
  let maxX = positions[0][0];
  for (let i = 1; i < n; i++) {
    const x = positions[i][0];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
  }
  const xRange = maxX - minX;
  if (xRange < 1e-9) return null;

  const scale = new Float32Array(targetSampleCount);
  const denom = targetSampleCount === 1 ? 1 : targetSampleCount - 1;

  for (let i = 0; i < targetSampleCount; i++) {
    const u = targetSampleCount === 1 ? 0 : i / denom;
    const targetX = minX + u * xRange;
    scale[i] = Math.max(0, sampleYAtX(positions, targetX));
  }
  return scale;
}

/**
 * Sample Y at a given X by walking the polyline of taper samples and
 * linearly interpolating across the bracketing pair. Handles non-monotonic
 * X gracefully by returning the last-bracket hit; most taper curves are
 * drawn left-to-right so monotonicity is typical.
 */
function sampleYAtX(
  positions: readonly [number, number, number][],
  x: number,
): number {
  const n = positions.length;
  for (let i = 0; i < n - 1; i++) {
    const x0 = positions[i][0];
    const x1 = positions[i + 1][0];
    const lo = Math.min(x0, x1);
    const hi = Math.max(x0, x1);
    if (x >= lo && x <= hi) {
      const span = x1 - x0;
      if (Math.abs(span) < 1e-12) return positions[i][1];
      const t = (x - x0) / span;
      return positions[i][1] + (positions[i + 1][1] - positions[i][1]) * t;
    }
  }
  // Out of range — clamp to nearest endpoint's Y value.
  const firstX = positions[0][0];
  const lastX = positions[n - 1][0];
  return Math.abs(x - firstX) < Math.abs(x - lastX)
    ? positions[0][1]
    : positions[n - 1][1];
}
